/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * État de chargement élégant pour l'analyse juridique IA :
 *  - Skeleton loader reproduisant la structure de la future consultation juridique
 *  - Message clair et sobre
 *  - Bouton d'annulation de requête
 */

import React from 'react';
import { Sparkles, XCircle } from 'lucide-react';

interface AiAnalysisLoadingStateProps {
  onCancel?: () => void;
  statusMessage?: string;
}

export const AiAnalysisLoadingState: React.FC<AiAnalysisLoadingStateProps> = ({
  onCancel,
  statusMessage = "Votre demande est en cours d'analyse. Recherche des textes et dispositions applicables dans le corpus Docarn Juridique...",
}) => {
  return (
    <div className="w-full max-w-4xl space-y-6 pt-2 animate-fade-in">
      {/* Banner de statut */}
      <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-100 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-violet-600 text-white flex items-center justify-center shrink-0 shadow-sm animate-pulse">
            <Sparkles className="w-4.5 h-4.5 text-amber-300" />
          </div>
          <div>
            <h4 className="text-xs sm:text-sm font-bold text-violet-950 flex items-center gap-2">
              <span>Analyse juridique en cours</span>
              <span className="w-2 h-2 rounded-full bg-violet-500 animate-ping" />
            </h4>
            <p className="text-[11px] sm:text-xs text-violet-700/90 font-medium">
              {statusMessage}
            </p>
          </div>
        </div>

        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-3.5 py-1.5 text-xs font-semibold text-zinc-600 hover:text-red-600 hover:bg-white rounded-lg transition border border-zinc-200/80 bg-white/70 shadow-2xs flex items-center gap-1.5 cursor-pointer shrink-0"
          >
            <XCircle className="w-3.5 h-3.5 text-zinc-400" />
            <span>Annuler</span>
          </button>
        )}
      </div>

      {/* Skeletons structurés simulant l'avis juridique */}
      <div className="bg-white rounded-2xl border border-zinc-200/80 p-6 sm:p-7 shadow-xs space-y-7">
        {/* Skeleton Bloc 1 : Synthèse */}
        <div className="space-y-2.5">
          <div className="h-3.5 w-44 bg-zinc-200 rounded-full animate-pulse" />
          <div className="h-4 w-full bg-zinc-100 rounded animate-pulse" />
          <div className="h-4 w-5/6 bg-zinc-100 rounded animate-pulse" />
        </div>

        {/* Skeleton Bloc 2 : Qualifications */}
        <div className="space-y-2">
          <div className="h-3 w-36 bg-zinc-200 rounded-full animate-pulse" />
          <div className="flex flex-wrap gap-2">
            <div className="h-6 w-24 bg-zinc-100 rounded-full animate-pulse" />
            <div className="h-6 w-32 bg-zinc-100 rounded-full animate-pulse" />
            <div className="h-6 w-28 bg-zinc-100 rounded-full animate-pulse" />
          </div>
        </div>

        {/* Skeleton Bloc 3 : Fondements */}
        <div className="space-y-3 pt-2">
          <div className="h-3.5 w-48 bg-zinc-200 rounded-full animate-pulse" />
          <div className="p-4 rounded-xl border border-zinc-100 space-y-2">
            <div className="h-4 w-2/3 bg-zinc-100 rounded animate-pulse" />
            <div className="h-3 w-full bg-zinc-50 rounded animate-pulse" />
          </div>
        </div>

        {/* Skeleton Bloc 4 : Analyse */}
        <div className="space-y-2.5 pt-2">
          <div className="h-3.5 w-40 bg-zinc-200 rounded-full animate-pulse" />
          <div className="space-y-2">
            <div className="h-3.5 w-full bg-zinc-100 rounded animate-pulse" />
            <div className="h-3.5 w-full bg-zinc-100 rounded animate-pulse" />
            <div className="h-3.5 w-3/4 bg-zinc-100 rounded animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiAnalysisLoadingState;
